import React, { useState, useEffect, useRef } from 'react'
import { Typography, Card, Table, Tag, Button, Space, message, Modal, Form, Input, Select, Statistic, Row, Col, Checkbox, InputNumber, Divider, Drawer, Timeline, Progress, List, Spin } from 'antd'
import { RobotOutlined, PlayCircleOutlined, ExperimentOutlined, ReloadOutlined, RocketOutlined, HistoryOutlined, CheckCircleOutlined, RollbackOutlined, EditOutlined, DeleteOutlined, SyncOutlined, LoadingOutlined } from '@ant-design/icons'
import api from '../services/api'

const { Title } = Typography
const { Option } = Select
const { TextArea } = Input

const AIModels = () => {
  const [models, setModels] = useState([])
  const [loading, setLoading] = useState(false)
  const [trainModalVisible, setTrainModalVisible] = useState(false)
  const [selectedModelType, setSelectedModelType] = useState(null)
  const [versionDrawerVisible, setVersionDrawerVisible] = useState(false)
  const [selectedModelName, setSelectedModelName] = useState(null)
  const [versions, setVersions] = useState([])
  const [versionsLoading, setVersionsLoading] = useState(false)
  const [form] = Form.useForm()
  const isMobile = window.innerWidth < 768
  const [runningPipeline, setRunningPipeline] = useState(false)
  const [progressModalVisible, setProgressModalVisible] = useState(false)
  const [currentExecutionId, setCurrentExecutionId] = useState(null)
  const [progressData, setProgressData] = useState(null)
  const progressIntervalRef = useRef(null)

  useEffect(() => {
    fetchModels()
  }, [])

  const fetchModels = async () => {
    setLoading(true)
    try {
      const data = await api.get('/ml/models')
      setModels(data)
    } catch (error) {
      console.error('Failed to fetch AI models:', error)
      message.error('Failed to load AI models')
    } finally {
      setLoading(false)
    }
  }

  const handleTrainModel = async (values) => {
    try {
      // Build training_params based on model type
      const training_params = {}

      if (values.model_type === 'xgboost') {
        if (values.xgboost_models && values.xgboost_models.length > 0) {
          training_params.models = values.xgboost_models
        }
        if (values.start_date) {
          training_params.start_date = values.start_date
        }
        if (values.end_date) {
          training_params.end_date = values.end_date
        }
      } else if (values.model_type === 'rl') {
        if (values.rl_strategies && values.rl_strategies.length > 0) {
          training_params.strategies = values.rl_strategies
        }
        if (values.rl_market_caps && values.rl_market_caps.length > 0) {
          training_params.market_caps = values.rl_market_caps
        }
        if (values.timesteps) {
          training_params.timesteps = values.timesteps
        }
      }

      const payload = {
        model_type: values.model_type,
        training_params,
        description: values.description
      }

      await api.post('/ml/train', payload)
      message.success(`${values.model_type.toUpperCase()} model training started successfully!`)
      setTrainModalVisible(false)
      form.resetFields()
      setSelectedModelType(null)

      // Refresh models list after a short delay
      setTimeout(() => fetchModels(), 1000)
    } catch (error) {
      console.error('Failed to start training:', error)
      message.error(`Failed to start model training: ${error.response?.data?.detail || error.message}`)
    }
  }

  const fetchVersionHistory = async (modelName) => {
    setVersionsLoading(true)
    try {
      const data = await api.get(`/ml/models/versions/${modelName}`)
      setVersions(data)
    } catch (error) {
      console.error('Failed to fetch version history:', error)
      message.error('Failed to load version history')
    } finally {
      setVersionsLoading(false)
    }
  }

  const handleViewVersions = (modelName) => {
    setSelectedModelName(modelName)
    setVersionDrawerVisible(true)
    fetchVersionHistory(modelName)
  }

  const handlePromoteVersion = async (deploymentId, modelName, modelVersion) => {
    try {
      await api.post(`/ml/models/${deploymentId}/promote`, {})
      message.success(`Model ${modelName} v${modelVersion} promoted to production!`)

      // Refresh both version history and models list
      fetchVersionHistory(modelName)
      fetchModels()
    } catch (error) {
      console.error('Failed to promote model:', error)
      message.error(`Failed to promote model: ${error.response?.data?.detail || error.message}`)
    }
  }

  const handleRetrainModel = (modelName, modelType) => {
    // Pre-fill the training modal with model info
    setSelectedModelType(modelType)
    setTrainModalVisible(true)

    // Set form values based on model name
    if (modelType === 'xgboost') {
      // Parse model name to get strategy and market cap
      const parts = modelName.split('_')
      if (parts.length >= 2) {
        form.setFieldsValue({
          model_type: 'xgboost',
          xgboost_models: [modelName],
          description: `Retraining ${modelName}`
        })
      }
    } else if (modelType === 'rl') {
      form.setFieldsValue({
        model_type: 'rl',
        description: `Retraining ${modelName}`
      })
    }

    message.info(`Ready to retrain ${modelName}`)
  }

  const handleViewMetrics = (record) => {
    const hasMetrics = record.performance_metrics && Object.keys(record.performance_metrics).length > 0

    Modal.info({
      title: `Performance Metrics - ${record.model_name}`,
      width: 600,
      content: (
        <div>
          <p><strong>Model Type:</strong> {record.model_type?.toUpperCase()}</p>
          <p><strong>Version:</strong> {record.model_version}</p>
          <p><strong>Status:</strong> <Tag color={
            record.deployment_status === 'production' ? 'green' :
            record.deployment_status === 'training' ? 'blue' :
            record.deployment_status === 'testing' ? 'orange' :
            record.deployment_status === 'retired' ? 'red' : 'default'
          }>{record.deployment_status?.toUpperCase()}</Tag></p>
          <Divider />
          <h4>Performance Metrics:</h4>
          {hasMetrics ? (
            <div>
              {Object.entries(record.performance_metrics).map(([key, value]) => (
                <Row key={key} style={{ padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Col span={12}><strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong></Col>
                  <Col span={12} style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                    {typeof value === 'number' ? value.toFixed(4) : value}
                  </Col>
                </Row>
              ))}
            </div>
          ) : (
            <div style={{
              background: '#fafafa',
              padding: '20px',
              borderRadius: '4px',
              textAlign: 'center',
              border: '1px dashed #d9d9d9'
            }}>
              <ExperimentOutlined style={{ fontSize: '32px', color: '#bfbfbf', marginBottom: '12px' }} />
              <p style={{ color: '#8c8c8c', margin: 0 }}>
                {record.deployment_status === 'training' ?
                  'Model is currently training. Metrics will be available after training completes.' :
                  'No performance metrics available for this model yet. Metrics are generated during training.'
                }
              </p>
            </div>
          )}
        </div>
      )
    })
  }

  const handleUpdateStatus = (record) => {
    const { deployment_id, model_name, deployment_status } = record

    Modal.confirm({
      title: `Update Status - ${model_name}`,
      content: (
        <div>
          <p>Current status: <Tag color={getStatusColor(deployment_status)}>{deployment_status?.toUpperCase()}</Tag></p>
          <p>Select new status:</p>
          <Select
            id="status-select"
            defaultValue={deployment_status}
            style={{ width: '100%', marginBottom: '12px' }}
          >
            <Option value="training">Training</Option>
            <Option value="testing">Testing</Option>
            <Option value="staging">Staging</Option>
            <Option value="production">Production</Option>
            <Option value="error">Error (for stuck jobs)</Option>
            <Option value="retired">Retired</Option>
          </Select>
          <Input.TextArea
            id="notes-input"
            placeholder="Optional notes about this status change..."
            rows={3}
          />
        </div>
      ),
      okText: 'Update Status',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const newStatus = document.getElementById('status-select').value
          const notes = document.getElementById('notes-input').value

          await api.put(`/ml/models/${deployment_id}/status`, {
            deployment_status: newStatus,
            notes: notes || undefined
          })

          message.success(`Model status updated from ${deployment_status} to ${newStatus}`)
          fetchModels()
        } catch (error) {
          console.error('Failed to update status:', error)
          message.error(`Failed to update status: ${error.response?.data?.detail || error.message}`)
        }
      }
    })
  }

  const handleRetireModel = async (deploymentId, modelName) => {
    Modal.confirm({
      title: 'Retire Model',
      content: `Are you sure you want to retire ${modelName}? This will mark it as retired and remove it from production.`,
      okText: 'Yes, Retire',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.put(`/ml/models/${deploymentId}/status`, {
            deployment_status: 'retired',
            notes: 'Retired via UI action'
          })
          message.success(`Model ${modelName} has been retired`)
          fetchModels()
        } catch (error) {
          console.error('Failed to retire model:', error)
          message.error(`Failed to retire model: ${error.response?.data?.detail || error.message}`)
        }
      }
    })
  }

  const handleRenameModel = (record) => {
    const { deployment_id, model_name } = record

    Modal.confirm({
      title: `Rename Model - ${model_name}`,
      content: (
        <div>
          <p>Current name: <strong>{model_name}</strong></p>
          <p>Enter new name:</p>
          <Input
            id="new-name-input"
            defaultValue={model_name}
            placeholder="Enter new model name"
            style={{ width: '100%' }}
          />
          <p style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '8px' }}>
            Only letters, numbers, underscores, and hyphens allowed
          </p>
        </div>
      ),
      okText: 'Rename',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const newName = document.getElementById('new-name-input').value.trim()

          if (!newName) {
            message.error('Model name cannot be empty')
            return
          }

          if (!/^[a-zA-Z0-9_-]+$/.test(newName)) {
            message.error('Model name can only contain letters, numbers, underscores, and hyphens')
            return
          }

          await api.put(`/ml/models/${deployment_id}/rename`, {
            new_name: newName
          })

          message.success(`Model renamed from "${model_name}" to "${newName}"`)
          fetchModels()
        } catch (error) {
          console.error('Failed to rename model:', error)
          message.error(`Failed to rename model: ${error.response?.data?.detail || error.message}`)
        }
      }
    })
  }

  const handleDeleteModel = async (deploymentId, modelName, modelStatus) => {
    Modal.confirm({
      title: 'Delete Model',
      content: (
        <div>
          <p>Are you sure you want to <strong>permanently delete</strong> {modelName}?</p>
          <p style={{ color: '#ff4d4f' }}>⚠️ This action cannot be undone!</p>
          <p style={{ fontSize: '12px', color: '#8c8c8c' }}>
            Note: This only deletes the database record. Model files on disk will remain.
          </p>
        </div>
      ),
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.delete(`/ml/models/${deploymentId}`)
          message.success(`Model ${modelName} has been deleted`)
          fetchModels()
        } catch (error) {
          console.error('Failed to delete model:', error)
          message.error(`Failed to delete model: ${error.response?.data?.detail || error.message}`)
        }
      }
    })
  }

  const fetchProgress = async (executionId) => {
    try {
      const data = await api.get(`/pipelines/execution/${executionId}/progress`)
      setProgressData(data)

      // Stop polling if completed or failed
      if (data.status === 'success' || data.status === 'failed') {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current)
          progressIntervalRef.current = null
        }
        setRunningPipeline(false)

        // Refresh models list
        fetchModels()

        // Show completion message
        if (data.status === 'success') {
          message.success('Training pipeline completed successfully!')
        } else {
          message.error('Training pipeline failed. Check the logs for details.')
        }
      }
    } catch (error) {
      console.error('Failed to fetch progress:', error)
    }
  }

  const handleRunWeeklyPipeline = () => {
    Modal.confirm({
      title: 'Run Weekly ML Training Pipeline',
      content: (
        <div>
          <p>This will train all ML models with the latest data:</p>
          <ul style={{ marginTop: '12px', marginBottom: '12px' }}>
            <li>Train ML models for all strategies (growth, value, dividend)</li>
            <li>Update model performance metrics</li>
            <li>Generate training reports</li>
          </ul>
          <p>Estimated duration: <strong>20-30 minutes</strong></p>
          <p style={{ color: '#1890ff', marginTop: '12px' }}>
            ℹ️ This trains machine learning models for portfolio optimization
          </p>
        </div>
      ),
      okText: 'Run Training',
      okButtonProps: { danger: false, type: 'primary' },
      onOk: async () => {
        try {
          setRunningPipeline(true)

          const data = await api.post('/pipelines/run-weekly', {})

          const executionId = data.execution_id
          setCurrentExecutionId(executionId)
          setProgressData(null)
          setProgressModalVisible(true)

          message.success('Weekly ML training started!')

          progressIntervalRef.current = setInterval(() => {
            fetchProgress(executionId)
          }, 2000)

          fetchProgress(executionId)
        } catch (error) {
          console.error('Failed to run weekly pipeline:', error)
          message.error(`Failed to start training: ${error.response?.data?.detail || error.message}`)
          setRunningPipeline(false)
        }
      }
    })
  }

  const handleRunMonthlyPipeline = () => {
    Modal.confirm({
      title: 'Run Monthly RL Training Pipeline',
      content: (
        <div>
          <p>This will train all RL models with the latest data:</p>
          <ul style={{ marginTop: '12px', marginBottom: '12px' }}>
            <li>Train reinforcement learning models</li>
            <li>Update RL policy networks</li>
            <li>Generate performance analytics</li>
          </ul>
          <p>Estimated duration: <strong>30-60 minutes</strong></p>
          <p style={{ color: '#722ed1', marginTop: '12px' }}>
            ℹ️ This trains deep RL models for advanced trading strategies
          </p>
        </div>
      ),
      okText: 'Run Training',
      okButtonProps: { danger: false, type: 'primary' },
      onOk: async () => {
        try {
          setRunningPipeline(true)

          const data = await api.post('/pipelines/run-monthly', {})

          const executionId = data.execution_id
          setCurrentExecutionId(executionId)
          setProgressData(null)
          setProgressModalVisible(true)

          message.success('Monthly RL training started!')

          progressIntervalRef.current = setInterval(() => {
            fetchProgress(executionId)
          }, 2000)

          fetchProgress(executionId)
        } catch (error) {
          console.error('Failed to run monthly pipeline:', error)
          message.error(`Failed to start training: ${error.response?.data?.detail || error.message}`)
          setRunningPipeline(false)
        }
      }
    })
  }

  useEffect(() => {
    // Cleanup interval on unmount
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
      }
    }
  }, [])

  const getStatusColor = (status) => {
    const colors = {
      'production': 'green',
      'training': 'blue',
      'testing': 'orange',
      'staging': 'cyan',
      'error': 'red',
      'failed': 'red',
      'retired': 'gray'
    }
    return colors[status] || 'default'
  }

  const columns = [
    {
      title: 'Model Name',
      dataIndex: 'model_name',
      key: 'model_name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Type',
      dataIndex: 'model_type',
      key: 'model_type',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: 'Version',
      dataIndex: 'model_version',
      key: 'model_version',
      render: (text) => <code>{text}</code>
    },
    {
      title: 'Status',
      dataIndex: 'deployment_status',
      key: 'deployment_status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase() || 'UNKNOWN'}
        </Tag>
      )
    },
    {
      title: 'Accuracy',
      dataIndex: 'performance_metrics',
      key: 'accuracy',
      render: (metrics) => {
        const accuracy = metrics?.accuracy || metrics?.test_accuracy
        return accuracy ? `${(accuracy * 100).toFixed(2)}%` : 'N/A'
      }
    },
    {
      title: 'Trained At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => date ? new Date(date).toLocaleString() : 'N/A'
    },
    {
      title: 'Deployed At',
      dataIndex: 'deployed_at',
      key: 'deployed_at',
      render: (date) => date ? new Date(date).toLocaleString() : 'Not deployed'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            size="small"
            icon={<HistoryOutlined />}
            onClick={() => handleViewVersions(record.model_name)}
          >
            {!isMobile && 'Versions'}
          </Button>
          <Button
            size="small"
            icon={<ExperimentOutlined />}
            onClick={() => handleViewMetrics(record)}
          >
            {!isMobile && 'Metrics'}
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleUpdateStatus(record)}
            type={record.deployment_status === 'training' ? 'primary' : 'default'}
          >
            {!isMobile && 'Status'}
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleRenameModel(record)}
          >
            {!isMobile && 'Rename'}
          </Button>
          {record.deployment_status === 'production' && (
            <>
              <Button
                size="small"
                type="primary"
                icon={<ReloadOutlined />}
                onClick={() => handleRetrainModel(record.model_name, record.model_type)}
              >
                {!isMobile && 'Retrain'}
              </Button>
              <Button
                size="small"
                danger
                icon={<RollbackOutlined />}
                onClick={() => handleRetireModel(record.deployment_id, record.model_name)}
              >
                {!isMobile && 'Retire'}
              </Button>
            </>
          )}
          {record.deployment_status === 'testing' && (
            <Button
              size="small"
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => handlePromoteVersion(record.deployment_id, record.model_name, record.model_version)}
            >
              {!isMobile && 'Promote'}
            </Button>
          )}
          {/* Delete button for non-production models */}
          {record.deployment_status !== 'production' && (
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteModel(record.deployment_id, record.model_name, record.deployment_status)}
            >
              {!isMobile && 'Delete'}
            </Button>
          )}
          {/* Retrain button for error/retired models */}
          {(record.deployment_status === 'error' || record.deployment_status === 'retired') && (
            <Button
              size="small"
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => handleRetrainModel(record.model_name, record.model_type)}
            >
              {!isMobile && 'Retrain'}
            </Button>
          )}
        </Space>
      )
    }
  ]

  // Calculate stats
  const stats = {
    total: models.length,
    production: models.filter(m => m.deployment_status === 'production').length,
    training: models.filter(m => m.deployment_status === 'training').length,
    testing: models.filter(m => m.deployment_status === 'testing').length
  }

  // Mobile-optimized columns
  const responsiveColumns = isMobile ? columns.filter(col =>
    ['model_name', 'deployment_status', 'actions'].includes(col.key)
  ) : columns

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <Title level={2} className="mb-0">AI Models</Title>
        <Space direction={isMobile ? "vertical" : "horizontal"} style={{ width: isMobile ? '100%' : 'auto' }} size="small" wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchModels}
            loading={loading}
            block={isMobile}
          >
            Refresh
          </Button>
          <Button
            type="default"
            icon={<SyncOutlined />}
            onClick={handleRunWeeklyPipeline}
            loading={runningPipeline}
            block={isMobile}
            style={{ borderColor: '#1890ff', color: '#1890ff' }}
          >
            Daily ML
          </Button>
          <Button
            type="default"
            icon={<RocketOutlined />}
            onClick={handleRunMonthlyPipeline}
            loading={runningPipeline}
            block={isMobile}
            style={{ borderColor: '#722ed1', color: '#722ed1' }}
          >
            Daily RL
          </Button>
          <Button
            type="primary"
            icon={<RobotOutlined />}
            onClick={() => setTrainModalVisible(true)}
            block={isMobile}
          >
            {isMobile ? 'Train Model' : 'Train New Model'}
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={12} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Models"
              value={stats.total}
              prefix={<RocketOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card>
            <Statistic
              title="In Production"
              value={stats.production}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Training"
              value={stats.training}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Testing"
              value={stats.testing}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ overflowX: 'auto' }}>
          <Table
            columns={responsiveColumns}
            dataSource={models}
            loading={loading}
            rowKey="deployment_id"
            scroll={{ x: isMobile ? 800 : undefined }}
            pagination={{
              pageSize: 10,
              simple: isMobile,
              showSizeChanger: !isMobile
            }}
          />
        </div>
      </Card>

      <Modal
        title="Train New Model"
        open={trainModalVisible}
        onCancel={() => {
          setTrainModalVisible(false)
          form.resetFields()
          setSelectedModelType(null)
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleTrainModel}
        >
          <Form.Item
            name="model_type"
            label="Model Type"
            rules={[{ required: true, message: 'Please select a model type' }]}
          >
            <Select
              placeholder="Select model type"
              onChange={(value) => setSelectedModelType(value)}
            >
              <Option value="xgboost">XGBoost (Stock Screening)</Option>
              <Option value="rl">Reinforcement Learning (Portfolio Optimization)</Option>
            </Select>
          </Form.Item>

          {selectedModelType === 'xgboost' && (
            <>
              <Divider>XGBoost Configuration</Divider>
              <Form.Item
                name="xgboost_models"
                label="Models to Train"
                tooltip="Select specific models or leave empty to train all 7 models"
              >
                <Select
                  mode="multiple"
                  placeholder="Select models (empty = train all 7 models)"
                  allowClear
                >
                  <Option value="growth_smallcap">Growth - Small Cap</Option>
                  <Option value="growth_midcap">Growth - Mid Cap</Option>
                  <Option value="growth_largecap">Growth - Large Cap</Option>
                  <Option value="value_smallcap">Value - Small Cap</Option>
                  <Option value="value_midcap">Value - Mid Cap</Option>
                  <Option value="value_largecap">Value - Large Cap</Option>
                  <Option value="dividend_strategy">Dividend Income (Mid/Large Cap)</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="start_date"
                label="Start Date"
                tooltip="Training data start date (default: 2015-01-01)"
              >
                <Input placeholder="2015-01-01" />
              </Form.Item>
              <Form.Item
                name="end_date"
                label="End Date"
                tooltip="Training data end date (default: today)"
              >
                <Input placeholder="Today" />
              </Form.Item>
            </>
          )}

          {selectedModelType === 'rl' && (
            <>
              <Divider>Reinforcement Learning Configuration</Divider>
              <Form.Item
                name="rl_strategies"
                label="Strategies"
                tooltip="Select strategies to train. All combinations of strategy × market cap will be trained. Empty = train growth+value for all caps (6 agents total)"
              >
                <Select
                  mode="multiple"
                  placeholder="Select strategies (empty = growth+value)"
                  allowClear
                >
                  <Option value="growth">Growth</Option>
                  <Option value="value">Value</Option>
                  <Option value="dividend">Dividend (no market cap needed)</Option>
                </Select>
              </Form.Item>

              {/* Only show market caps field if dividend is NOT selected */}
              <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.rl_strategies !== currentValues.rl_strategies}>
                {({ getFieldValue }) => {
                  const strategies = getFieldValue('rl_strategies') || []
                  const hasDividend = strategies.includes('dividend')
                  const hasOtherStrategies = strategies.some(s => s !== 'dividend')

                  // Hide market caps if only dividend is selected
                  if (hasDividend && !hasOtherStrategies) {
                    return null
                  }

                  return (
                    <Form.Item
                      name="rl_market_caps"
                      label="Market Caps"
                      tooltip="Select market caps to train. All combinations of strategy × market cap will be trained. Empty = train all caps (small, mid, large). Note: Dividend strategy doesn't use market caps."
                    >
                      <Select
                        mode="multiple"
                        placeholder="Select market caps (empty = all 3)"
                        allowClear
                      >
                        <Option value="small">Small Cap</Option>
                        <Option value="mid">Mid Cap</Option>
                        <Option value="large">Large Cap</Option>
                      </Select>
                    </Form.Item>
                  )
                }}
              </Form.Item>
              <Form.Item
                name="timesteps"
                label="Training Timesteps"
                tooltip="Number of training timesteps (default: 100,000, reduced from 500k to prevent OOM)"
                initialValue={100000}
              >
                <InputNumber
                  min={100000}
                  max={5000000}
                  step={100000}
                  style={{ width: '100%' }}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/,/g, '')}
                />
              </Form.Item>
            </>
          )}

          <Divider />

          <Form.Item
            name="description"
            label="Description (Optional)"
          >
            <TextArea
              rows={3}
              placeholder="Optional description for this training run"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" disabled={!selectedModelType}>
                Start Training
              </Button>
              <Button onClick={() => {
                setTrainModalVisible(false)
                form.resetFields()
                setSelectedModelType(null)
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={`Version History: ${selectedModelName}`}
        placement="right"
        width={720}
        open={versionDrawerVisible}
        onClose={() => setVersionDrawerVisible(false)}
      >
        {versionsLoading ? (
          <div className="flex justify-center items-center h-64">
            <Space direction="vertical" align="center">
              <ReloadOutlined spin style={{ fontSize: 32 }} />
              <div>Loading version history...</div>
            </Space>
          </div>
        ) : (
          <Timeline mode="left">
            {versions.map((version, index) => {
              const isProduction = version.deployment_status === 'production'
              const isTesting = version.deployment_status === 'testing'
              const isRetired = version.deployment_status === 'retired'
              const isTraining = version.deployment_status === 'training'

              return (
                <Timeline.Item
                  key={version.deployment_id}
                  color={isProduction ? 'green' : isTesting ? 'orange' : isTraining ? 'blue' : 'gray'}
                  dot={isProduction ? <CheckCircleOutlined style={{ fontSize: 16 }} /> : undefined}
                  label={
                    <div>
                      <div><strong>v{version.model_version}</strong></div>
                      <div className="text-xs text-gray-500">
                        {new Date(version.created_at).toLocaleString()}
                      </div>
                    </div>
                  }
                >
                  <Card size="small">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div className="flex justify-between items-center">
                        <Space>
                          <Tag color={getStatusColor(version.deployment_status)}>
                            {version.deployment_status?.toUpperCase()}
                          </Tag>
                          <Tag color="blue">{version.model_type}</Tag>
                        </Space>
                        {isTesting && (
                          <Button
                            type="primary"
                            size="small"
                            icon={<CheckCircleOutlined />}
                            onClick={() => handlePromoteVersion(version.deployment_id, version.model_name, version.model_version)}
                          >
                            Promote to Production
                          </Button>
                        )}
                        {isRetired && (
                          <Button
                            size="small"
                            icon={<RollbackOutlined />}
                            onClick={() => handlePromoteVersion(version.deployment_id, version.model_name, version.model_version)}
                          >
                            Rollback to This Version
                          </Button>
                        )}
                      </div>

                      {version.performance_metrics && Object.keys(version.performance_metrics).length > 0 && (
                        <div>
                          <strong className="text-xs text-gray-600">Performance Metrics:</strong>
                          <div className="mt-1 pl-2 border-l-2 border-gray-200">
                            {Object.entries(version.performance_metrics).slice(0, 5).map(([key, value]) => (
                              <div key={key} className="text-xs">
                                <span className="text-gray-600">{key}:</span>{' '}
                                <span className="font-mono">
                                  {typeof value === 'number' ? value.toFixed(4) : value}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {version.deployed_at && (
                        <div className="text-xs text-gray-500">
                          Deployed: {new Date(version.deployed_at).toLocaleString()}
                        </div>
                      )}
                    </Space>
                  </Card>
                </Timeline.Item>
              )
            })}
          </Timeline>
        )}
      </Drawer>

      {/* Training Pipeline Progress Modal */}
      <Modal
        title={
          <Space>
            <RocketOutlined />
            Training Pipeline Progress
          </Space>
        }
        open={progressModalVisible}
        onCancel={() => {
          setProgressModalVisible(false)
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current)
            progressIntervalRef.current = null
          }
        }}
        footer={null}
        width={700}
        closable={progressData?.status === 'success' || progressData?.status === 'failed'}
        maskClosable={false}
      >
        {progressData ? (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <Progress
                percent={
                  progressData.progress
                    ? Math.round((progressData.progress.current_step / progressData.progress.total_steps) * 100)
                    : 0
                }
                status={
                  progressData.status === 'running' ? 'active' :
                  progressData.status === 'success' ? 'success' :
                  progressData.status === 'failed' ? 'exception' : 'normal'
                }
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
              <div style={{ marginTop: '8px', textAlign: 'center' }}>
                <Typography.Text strong>
                  {progressData.progress ? (
                    <>
                      Step {progressData.progress.current_step} of {progressData.progress.total_steps}
                      {progressData.status === 'running' && ' - ' + progressData.progress.description}
                    </>
                  ) : (
                    'Initializing...'
                  )}
                </Typography.Text>
              </div>
            </div>

            {progressData.progress && (
              <div style={{ marginBottom: '16px' }}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Typography.Text type="secondary">Current Step:</Typography.Text>
                  <Typography.Text strong>{progressData.progress?.description || progressData.progress?.current_script || 'Starting...'}</Typography.Text>

                  {progressData.progress.models_to_train && (
                    <div style={{ marginTop: '8px' }}>
                      <Typography.Text type="secondary">Models Being Trained:</Typography.Text>
                      <div style={{ marginTop: '4px' }}>
                        {progressData.progress.models_to_train.map((model, idx) => (
                          <Tag key={idx} color="cyan" style={{ marginBottom: '4px' }}>
                            {model}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )}

                  {progressData.progress.total_models && (
                    <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                      Training {progressData.progress.total_models} total models
                    </Typography.Text>
                  )}
                </Space>
              </div>
            )}

            {progressData.progress?.failed_scripts && progressData.progress.failed_scripts.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <Typography.Text type="danger" strong>Failed Steps:</Typography.Text>
                <List
                  size="small"
                  dataSource={progressData.progress.failed_scripts}
                  renderItem={item => (
                    <List.Item>
                      <Space direction="vertical" size={0} style={{ width: '100%' }}>
                        <Typography.Text type="danger">{item.script}</Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                          {item.error.substring(0, 200)}...
                        </Typography.Text>
                      </Space>
                    </List.Item>
                  )}
                  style={{ marginTop: '8px' }}
                />
              </div>
            )}

            <div style={{ marginTop: '24px', padding: '12px', background: '#f0f5ff', borderRadius: '4px' }}>
              <Space direction="vertical" size={4}>
                {progressData.pipeline_name && (
                  <Space>
                    <Typography.Text type="secondary">Pipeline:</Typography.Text>
                    <Tag color="blue">
                      {progressData.pipeline_name === 'weekly_ml_training' ? 'Weekly ML Training (LightGBM GPU)' :
                       progressData.pipeline_name === 'monthly_rl_training' ? 'Monthly RL Training (Portfolio Optimization)' :
                       progressData.pipeline_name}
                    </Tag>
                  </Space>
                )}
                <Space>
                  <Typography.Text type="secondary">Status:</Typography.Text>
                  <Tag color={
                    progressData.status === 'running' ? 'processing' :
                    progressData.status === 'success' ? 'success' :
                    'error'
                  }>
                    {progressData.status.toUpperCase()}
                  </Tag>
                </Space>
                <Space>
                  <Typography.Text type="secondary">Started:</Typography.Text>
                  <Typography.Text>{new Date(progressData.started_at).toLocaleString()}</Typography.Text>
                </Space>
                {progressData.completed_at && (
                  <Space>
                    <Typography.Text type="secondary">Completed:</Typography.Text>
                    <Typography.Text>{new Date(progressData.completed_at).toLocaleString()}</Typography.Text>
                  </Space>
                )}
                {progressData.duration_seconds && (
                  <Space>
                    <Typography.Text type="secondary">Duration:</Typography.Text>
                    <Typography.Text>{Math.floor(progressData.duration_seconds / 60)}m {progressData.duration_seconds % 60}s</Typography.Text>
                  </Space>
                )}
              </Space>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" tip="Starting training pipeline..." />
          </div>
        )}
      </Modal>
    </div>
  )
}

export default AIModels
